import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

// utils
import { FORM, SALONS_FILTER_TYPE } from '../../utils/enums'
import { SALONS_FILTER_ITEMS_CONFIG } from '../../utils/helper'

// hooks
import useMessages from '../../hooks/useMessages'

// components
import Drawer from '../Drawer/Drawer'
import SidebarFooter from '../FilterSidebarFooter/FilterSidebarFooter'
import Tag from '../../atoms/Tag/Tag'
import HookFormField from '../../formFields/HookFormField'
import PriceRangeFormField from '../../formFields/PriceRangeFormField/PriceRangeFormField'

// types
import { Currency, FilterPriceValuesType, FilterValuesType, GetSalonsFilterCountResponse, LoadSalonsFromFiltersParams } from '../../types/types'

// assets
import ChevronDownLeft from '../../assets/icons/ChevronLeftIcon'

// styles
import * as SC from './FilterSidebarStyles'

// types
type FilterSidebarPriceProps = {
	filterSidebarDetailOpen: boolean
	setFilterSidebarDetailOpen: (open: boolean) => void
	filterDetailOpenedDirectly: boolean
	loadSalonsFromFilters: (params: LoadSalonsFromFiltersParams) => void
	setFilterValues: (newFilterValues: FilterPriceValuesType) => void
	currency: Currency
	currencySecondary: Currency | undefined
	filterItemsConfig: ReturnType<typeof SALONS_FILTER_ITEMS_CONFIG>
	submittedFilterValues: FilterValuesType
	filterCounts: GetSalonsFilterCountResponse['priceRange'] | undefined
	defaultValues: {
		priceFrom: number | undefined
		priceTo: number | undefined
		priceToSecondary: number | undefined
	}
	filterDetailDrawerId: string
}

// component
const FilterSidebarPrice = (props: FilterSidebarPriceProps) => {
	const {
		filterSidebarDetailOpen,
		setFilterSidebarDetailOpen,
		filterDetailOpenedDirectly,
		loadSalonsFromFilters,
		setFilterValues,
		currency,
		currencySecondary,
		filterItemsConfig,
		submittedFilterValues,
		defaultValues,
		filterCounts,
		filterDetailDrawerId
	} = props
	const { messages } = useMessages()
	const { formatMessage } = useIntl()

	// default filter values
	const serviceTotalPriceFromDefaultValue = defaultValues.priceFrom
	const serviceTotalPriceToDefaultValue = defaultValues.priceTo
	const serviceTotalPriceToSecondaryDefaultValue = defaultValues.priceToSecondary

	// submitted filter values
	const submittedServiceTotalPriceFrom = submittedFilterValues.serviceTotalPriceRange.priceFrom ?? serviceTotalPriceFromDefaultValue
	const submittedServiceTotalPriceTo = submittedFilterValues.serviceTotalPriceRange.priceTo ?? serviceTotalPriceToDefaultValue

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { isDirty }
	} = useForm<FilterPriceValuesType>({
		defaultValues: {
			serviceTotalPriceRange: {
				priceFrom: submittedServiceTotalPriceFrom,
				priceTo: submittedServiceTotalPriceTo
			}
		}
	})

	const { priceFrom: serviceTotalPriceFrom, priceTo: serviceTotalPriceTo } = watch('serviceTotalPriceRange')

	// check if current internal filter values are equal to default values
	// we will show "reset" buttons based on the check
	const isPriceFilterEqualToDefault = serviceTotalPriceFrom === serviceTotalPriceFromDefaultValue && serviceTotalPriceTo === serviceTotalPriceToDefaultValue

	useEffect(() => {
		// reinitialize internal filter values when submitted values changes to keep them in sync
		reset({
			serviceTotalPriceRange: {
				priceFrom: submittedServiceTotalPriceFrom,
				priceTo: submittedServiceTotalPriceTo
			}
		})
	}, [submittedServiceTotalPriceFrom, submittedServiceTotalPriceTo, reset])

	const handleSubmitFilterDetail = (formSubmitValues: FilterPriceValuesType) => {
		const {
			serviceTotalPriceRange: { priceFrom: priceFromSubmitValue, priceTo: priceToSubmitValue }
		} = formSubmitValues

		if (filterDetailOpenedDirectly) {
			const params: LoadSalonsFromFiltersParams = {
				serviceTotalPriceFrom: priceFromSubmitValue === serviceTotalPriceFromDefaultValue ? null : priceFromSubmitValue,
				serviceTotalPriceTo: priceToSubmitValue === serviceTotalPriceToDefaultValue ? null : priceToSubmitValue
			}

			loadSalonsFromFilters(params)
		} else {
			setFilterValues({ serviceTotalPriceRange: { priceFrom: priceFromSubmitValue, priceTo: priceToSubmitValue } })
		}

		setFilterSidebarDetailOpen(false)
	}

	const resetPriceFilterToDefault = () => {
		setValue(
			'serviceTotalPriceRange',
			{
				priceFrom: serviceTotalPriceFromDefaultValue,
				priceTo: serviceTotalPriceToDefaultValue
			},
			{ shouldDirty: true }
		)
	}

	const closeSidebarDetail = () => {
		setFilterSidebarDetailOpen(false)
		reset()
	}

	const getFooterSliderItems = () => {
		const selectedPriceTitle = filterItemsConfig[SALONS_FILTER_TYPE.PRICE].getSelectedTitle({
			currencyCode: currency.code,
			minPrice: filterCounts?.minValue,
			maxPrice: filterCounts?.maxValue,
			maxPriceSecondary: filterCounts?.maxValueSecondary,
			priceFrom: serviceTotalPriceFrom,
			priceTo: serviceTotalPriceTo,
			currencyCodeSecondary: currencySecondary?.code
		})

		return selectedPriceTitle ? (
			<Tag
				key={SALONS_FILTER_TYPE.PRICE}
				label={selectedPriceTitle}
				closeAriaLabel={formatMessage(
					{ id: 'Remove filter {filterName}', defaultMessage: 'Remove filter {filterName}' },
					{ filterName: selectedPriceTitle }
				)}
				onCloseBtnClick={resetPriceFilterToDefault}
			/>
		) : null
	}

	return (
		<Drawer
			id={filterDetailDrawerId}
			open={filterSidebarDetailOpen}
			mask
			maskClosable
			// if the detail is open from the main filter sidebar, it already has a mask, so we need to set opacity of detail sidebar mask to zero, so they don't overlap visually
			maskStyle={!filterDetailOpenedDirectly ? { opacity: 0 } : undefined}
			closeIcon={!filterDetailOpenedDirectly ? <ChevronDownLeft /> : undefined}
			closeAriaLabel={!filterDetailOpenedDirectly ? formatMessage({ id: 'Back to filters', defaultMessage: 'Back to filters' }) : undefined}
			title={filterItemsConfig[SALONS_FILTER_TYPE.PRICE].title}
			onClose={closeSidebarDetail}
			headerExtra={
				!isPriceFilterEqualToDefault ? <SC.HeaderResetButton onClick={resetPriceFilterToDefault}>{messages.Reset}</SC.HeaderResetButton> : undefined
			}
			footer={<SidebarFooter isVisible={isDirty} extraContent={getFooterSliderItems()} formId={FORM.FILTER_SIDEBAR_PRICE} />}
			destroyOnClose
		>
			<SC.SidebarDetailContent>
				<SC.SidebarForm onSubmit={handleSubmit(handleSubmitFilterDetail)} id={FORM.FILTER_SIDEBAR_PRICE}>
					<HookFormField
						control={control}
						name={'serviceTotalPriceRange'}
						component={PriceRangeFormField}
						priceFromDefaultValue={serviceTotalPriceFromDefaultValue}
						priceToDefaultValue={serviceTotalPriceToDefaultValue}
						currency={currency}
						priceToSecondaryDefaultValue={serviceTotalPriceToSecondaryDefaultValue}
						currencySecondary={currencySecondary}
					/>
				</SC.SidebarForm>
			</SC.SidebarDetailContent>
		</Drawer>
	)
}

export default FilterSidebarPrice
